/**
 * CarrierSequenceContext — shared carrier ordering state across Present sub-tabs.
 * Mounted in PresentWrapper, wrapping Quotes, Comps, Financial, and Responses.
 * Persists carrier order in localStorage keyed by RFP ID + coverage type, so
 * it survives page refreshes and new tabs.
 *
 * State updates go through a single reducer rather than several independent
 * `useState`s — a `setSequenceState` call used to fire four separate setters
 * sequentially, which is exactly the kind of uncoordinated multi-step update
 * that let CD-1419's drag-reorder race slip through elsewhere in the Quotes
 * tab. One dispatch keeps a commit atomic from a consumer's point of view.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from "react";

import type { CarrierSequencePreset } from "@platform/mpx-quotes-grid";

export type CarrierSequenceScope = "all" | "quotes-only";

export interface CarrierSequenceState {
  carrierIds: string[];
  appliedPreset: CarrierSequencePreset | null;
  scope: CarrierSequenceScope;
}

export interface CarrierSequenceContextValue {
  getSequenceState: (coverageType: string) => CarrierSequenceState;
  setSequenceState: (coverageType: string, state: CarrierSequenceState) => void;
  isAppliedToAllPages: (coverageType: string) => boolean;
  confirmApplyToAllPages: (coverageType: string) => void;
  appliedToAllCarrierOrder: string[];
  /**
   * Sets the shared cross-LOC carrier order directly, independent of any
   * single coverage's own order. A coverage's carrier list is only ever a
   * subset of the full RFP's carriers, so the caller is expected to already
   * have merged its own reorder into the *complete* order (e.g. via
   * `applyCarrierMoveToOrder`) — this just persists and broadcasts it.
   */
  setAppliedToAllCarrierOrder: (order: string[]) => void;
}

// Versioned so a stored order written by an earlier ordering rule is orphaned
// rather than applied. v1 entries were written by a bug that published the
// Quotes tab's own default order on any AG Grid column rebuild, so most of them
// hold an order no analyst chose; reading one back would override the
// alphabetical default on every tab. Bump this whenever the default order or
// the stored shape changes.
const STORAGE_KEY_PREFIX = "carrier-sequence-v2-";

const buildStorageKey = (rfpId: string, coverageType: string): string =>
  `${STORAGE_KEY_PREFIX}${rfpId}-${coverageType}`;

const buildAllPagesKey = (rfpId: string): string =>
  `${STORAGE_KEY_PREFIX}${rfpId}-__all-pages__`;

/**
 * The persisted "applied to all SBS pages" carrier order for an RFP — the same
 * order the provider seeds its state from. Exported for the Generate & Export
 * drawer, which mounts outside this provider but must send the analyst's
 * on-screen carrier order with the export request (CD-1992).
 */
export const loadAllPagesOrder = (rfpId: string): string[] => {
  try {
    const raw = localStorage.getItem(buildAllPagesKey(rfpId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
};

const saveAllPagesOrder = (rfpId: string, carrierIds: string[]): void => {
  try {
    localStorage.setItem(buildAllPagesKey(rfpId), JSON.stringify(carrierIds));
  } catch {
    // localStorage quota exceeded — silently ignore
  }
};

const loadFromStorage = (
  rfpId: string,
  coverageType: string,
): CarrierSequenceState | null => {
  try {
    const raw = localStorage.getItem(buildStorageKey(rfpId, coverageType));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CarrierSequenceState;
    return Array.isArray(parsed.carrierIds) ? parsed : null;
  } catch {
    return null;
  }
};

const saveToStorage = (
  rfpId: string,
  coverageType: string,
  state: CarrierSequenceState,
): void => {
  try {
    localStorage.setItem(
      buildStorageKey(rfpId, coverageType),
      JSON.stringify(state),
    );
  } catch {
    // localStorage quota exceeded — silently ignore
  }
};

const DEFAULT_STATE: CarrierSequenceState = {
  carrierIds: [],
  appliedPreset: null,
  scope: "all",
};

// ---------------------------------------------------------------------------
// Reducer — one dispatch per consumer-facing state change
// ---------------------------------------------------------------------------

interface ReducerState {
  stateMap: Record<string, CarrierSequenceState>;
  appliedToAllMap: Record<string, boolean>;
  appliedToAllCarrierOrder: string[];
}

type ReducerAction =
  | {
      type: "SET_SEQUENCE_STATE";
      coverageType: string;
      state: CarrierSequenceState;
    }
  | { type: "CONFIRM_APPLY_TO_ALL_PAGES"; coverageType: string }
  | { type: "SET_GLOBAL_ORDER"; order: string[] };

const reducer = (state: ReducerState, action: ReducerAction): ReducerState => {
  switch (action.type) {
    case "SET_SEQUENCE_STATE": {
      const { coverageType, state: nextState } = action;
      const appliedToAll = nextState.scope === "all";
      return {
        ...state,
        stateMap: { ...state.stateMap, [coverageType]: nextState },
        appliedToAllMap: {
          ...state.appliedToAllMap,
          [coverageType]: appliedToAll,
        },
      };
    }
    case "CONFIRM_APPLY_TO_ALL_PAGES": {
      if (state.appliedToAllMap[action.coverageType]) return state;
      return {
        ...state,
        appliedToAllMap: {
          ...state.appliedToAllMap,
          [action.coverageType]: true,
        },
      };
    }
    case "SET_GLOBAL_ORDER": {
      return { ...state, appliedToAllCarrierOrder: action.order };
    }
    default:
      return state;
  }
};

const createInitialState = (rfpId: string): ReducerState => ({
  stateMap: {},
  appliedToAllMap: {},
  appliedToAllCarrierOrder: loadAllPagesOrder(rfpId),
});

const CarrierSequenceContext =
  createContext<CarrierSequenceContextValue | null>(null);

interface CarrierSequenceProviderProps {
  rfpId: string;
  children: React.ReactNode;
}

export const CarrierSequenceProvider = ({
  rfpId,
  children,
}: CarrierSequenceProviderProps): React.JSX.Element => {
  const [state, dispatch] = useReducer(reducer, rfpId, createInitialState);
  const rfpIdRef = useRef(rfpId);
  rfpIdRef.current = rfpId;

  // Cache of localStorage values already loaded, so getSequenceState can
  // return them synchronously without dispatching during render.
  const storageCacheRef = useRef<Record<string, CarrierSequenceState>>({});

  const getSequenceState = useCallback(
    (coverageType: string): CarrierSequenceState => {
      if (state.stateMap[coverageType]) return state.stateMap[coverageType];
      if (storageCacheRef.current[coverageType])
        return storageCacheRef.current[coverageType];
      const saved = loadFromStorage(rfpIdRef.current, coverageType);
      if (saved) {
        storageCacheRef.current[coverageType] = saved;
        return saved;
      }
      return DEFAULT_STATE;
    },
    [state.stateMap],
  );

  // Persists this coverage type's own order only. The cross-LOC order shown
  // on Responses/Comps/Financials is a separate, explicitly-merged value —
  // see setAppliedToAllCarrierOrder — since a coverage's own carrierIds is
  // only ever a subset of the full RFP's carriers.
  const setSequenceState = useCallback(
    (coverageType: string, nextState: CarrierSequenceState): void => {
      storageCacheRef.current[coverageType] = nextState;
      saveToStorage(rfpIdRef.current, coverageType, nextState);
      dispatch({ type: "SET_SEQUENCE_STATE", coverageType, state: nextState });
    },
    [],
  );

  const isAppliedToAllPages = useCallback(
    (coverageType: string): boolean => !!state.appliedToAllMap[coverageType],
    [state.appliedToAllMap],
  );

  const confirmApplyToAllPages = useCallback((coverageType: string): void => {
    dispatch({ type: "CONFIRM_APPLY_TO_ALL_PAGES", coverageType });
  }, []);

  const setAppliedToAllCarrierOrder = useCallback((order: string[]): void => {
    saveAllPagesOrder(rfpIdRef.current, order);
    dispatch({ type: "SET_GLOBAL_ORDER", order });
  }, []);

  const value = useMemo<CarrierSequenceContextValue>(
    () => ({
      getSequenceState,
      setSequenceState,
      isAppliedToAllPages,
      confirmApplyToAllPages,
      appliedToAllCarrierOrder: state.appliedToAllCarrierOrder,
      setAppliedToAllCarrierOrder,
    }),
    [
      getSequenceState,
      setSequenceState,
      isAppliedToAllPages,
      confirmApplyToAllPages,
      state.appliedToAllCarrierOrder,
      setAppliedToAllCarrierOrder,
    ],
  );

  return (
    <CarrierSequenceContext.Provider value={value}>
      {children}
    </CarrierSequenceContext.Provider>
  );
};

export const useCarrierSequenceContext = (): CarrierSequenceContextValue => {
  const context = useContext(CarrierSequenceContext);
  if (!context) {
    throw new Error(
      "useCarrierSequenceContext must be used within a CarrierSequenceProvider",
    );
  }
  return context;
};
